import { useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, CardBody, ConfirmDialog, DataTable, EmptyState, Input, LoadingState, type Column } from "@agentnative/ui";
import { apiErrorMessage } from "../lib/api/client";
import { OneTimeSecretAlert } from "../components/OneTimeSecret";
import {
  MAX_REDIRECT_URIS,
  createOperatorClient,
  deleteOperatorClient,
  listOperatorClients,
  redirectUriProblem,
  rotateOperatorClientSecret,
  setOperatorClientDisabled,
  updateOperatorClient,
  type OneTimeSecret,
  type OperatorClient,
} from "../lib/api/oauth";

interface FormState {
  clientId: string | null; // null: creating
  newClientId: string;
  name: string;
  isPublic: boolean;
  uris: string[];
  shareOrgs: boolean;
  refreshToken: boolean;
  /** Redirect addresses already saved; they are not re-checked on the client (the server grandfathers them). */
  saved: string[];
}

const emptyForm: FormState = {
  clientId: null,
  newClientId: "",
  name: "",
  isPublic: false,
  uris: [""],
  shareOrgs: false,
  refreshToken: false,
  saved: [],
};

type Pending = { kind: "disable" | "delete" | "rotate"; client: OperatorClient } | null;

export function AdminOAuthRegisteredClientsRoute() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [secret, setSecret] = useState<OneTimeSecret | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["oauth-operator-clients"] });

  const clientsQuery = useQuery({ queryKey: ["oauth-operator-clients"], queryFn: listOperatorClients });

  const uris = form ? form.uris.map((u) => u.trim()) : [];
  const uriProblems = uris.map((u, i) => {
    if (form && form.saved.includes(u)) return null;
    const p = redirectUriProblem(u);
    if (p) return p;
    if (uris.indexOf(u) !== i) return "Duplicate address.";
    return null;
  });
  const idProblem =
    form && form.clientId === null && form.newClientId !== "" && !/^[a-z0-9][a-z0-9-]{2,62}$/.test(form.newClientId)
      ? "3 to 63 characters: a-z, 0-9 and hyphen."
      : null;
  const formValid =
    !!form && form.name.trim() !== "" && !idProblem && uris.length >= 1 && uris.length <= MAX_REDIRECT_URIS && uriProblems.every((p) => p === null);

  const saveMutation = useMutation({
    mutationFn: async (f: FormState) => {
      const redirect_uris = f.uris.map((u) => u.trim());
      if (f.clientId) {
        await updateOperatorClient(f.clientId, { name: f.name.trim(), redirect_uris, share_orgs: f.shareOrgs, refresh_token: f.refreshToken });
        return null;
      }
      return createOperatorClient({
        client_id: f.newClientId || undefined,
        name: f.name.trim(),
        public: f.isPublic,
        redirect_uris,
        share_orgs: f.shareOrgs,
        refresh_token: f.refreshToken,
      });
    },
    onSuccess: (created) => {
      setForm(null);
      if (created?.client_secret) setSecret({ client_id: created.client.client_id, client_secret: created.client_secret, message: created.message ?? "" });
      refresh();
    },
  });
  const actionMutation = useMutation({
    mutationFn: async (p: NonNullable<Pending>) => {
      if (p.kind === "rotate") {
        setSecret(await rotateOperatorClientSecret(p.client.client_id));
        return `A new secret was generated for ${p.client.name}. The old one no longer works.`;
      }
      if (p.kind === "delete") {
        await deleteOperatorClient(p.client.client_id);
        return `${p.client.name} was deleted.`;
      }
      const r = await setOperatorClientDisabled(p.client.client_id, true);
      return `${p.client.name} was disabled and ${r.grants_revoked} grant(s) were revoked.`;
    },
    onSuccess: (msg) => {
      setPending(null);
      setNotice(msg);
      refresh();
    },
  });
  const enableMutation = useMutation({
    mutationFn: (c: OperatorClient) => setOperatorClientDisabled(c.client_id, false),
    onSuccess: refresh,
  });

  function edit(c: OperatorClient) {
    setForm({
      clientId: c.client_id,
      newClientId: c.client_id,
      name: c.name,
      isPublic: c.public,
      uris: [...c.redirect_uris],
      shareOrgs: c.share_orgs,
      refreshToken: c.refresh_token,
      saved: [...c.redirect_uris],
    });
  }

  const columns: Column<OperatorClient>[] = [
    {
      key: "name",
      label: "App",
      render: (c) => (
        <div>
          <div className="font-medium">{c.name}</div>
          <div className="font-mono text-xs text-[var(--color-neutral-500)]">{c.client_id}</div>
        </div>
      ),
    },
    {
      key: "redirects",
      label: "Redirect addresses",
      render: (c) => (
        <ul className="font-mono text-xs">
          {c.redirect_uris.map((u) => (
            <li key={u} className="break-all">
              {u}
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: "state",
      label: "Type",
      render: (c) => (
        <div className="flex flex-wrap gap-1">
          <Badge variant="info">{c.public ? "Public (PKCE)" : "Confidential"}</Badge>
          {c.share_orgs && <Badge variant="warning">Reads memberships</Badge>}
          {c.disabled ? <Badge variant="danger">Disabled</Badge> : <Badge variant="success">Enabled</Badge>}
        </div>
      ),
    },
    { key: "grants", label: "Active grants", render: (c) => String(c.active_grants) },
    {
      key: "actions",
      label: "",
      render: (c) => (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => edit(c)} aria-label={`Edit ${c.name}`}>
            Edit
          </Button>
          {!c.public && (
            <Button size="sm" variant="secondary" onClick={() => setPending({ kind: "rotate", client: c })} aria-label={`Rotate secret of ${c.name}`}>
              Rotate secret
            </Button>
          )}
          {c.disabled ? (
            <Button size="sm" variant="secondary" disabled={enableMutation.isPending} onClick={() => enableMutation.mutate(c)} aria-label={`Enable ${c.name}`}>
              Enable
            </Button>
          ) : (
            <Button size="sm" variant="secondary" onClick={() => setPending({ kind: "disable", client: c })} aria-label={`Disable ${c.name}`}>
              Disable
            </Button>
          )}
          {c.disabled && (
            <Button size="sm" variant="danger" onClick={() => setPending({ kind: "delete", client: c })} aria-label={`Delete ${c.name}`}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  const mutationError = [saveMutation, actionMutation, enableMutation].find((m) => m.isError);
  const pendingText: Record<"disable" | "delete" | "rotate", { title: string; confirm: string; message: (c: OperatorClient) => string }> = {
    disable: {
      title: "Disable app",
      confirm: "Disable",
      message: (c) => `${c.name} has ${c.active_grants} active grant(s). Disabling stops every sign-in and token flow and revokes all of them.`,
    },
    delete: { title: "Delete app", confirm: "Delete", message: (c) => `Delete ${c.name}? This cannot be undone.` },
    rotate: {
      title: "Rotate secret",
      confirm: "Rotate",
      message: (c) => `Generate a new secret for ${c.name}? The current secret stops working immediately; update the app with the new one.`,
    },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Registered OAuth apps</h1>
        <Button onClick={() => setForm({ ...emptyForm, uris: [""] })} disabled={form !== null}>
          Register an app
        </Button>
      </div>
      <p className="text-sm text-[var(--color-neutral-500)]">
        Apps an operator has vouched for, such as LedgerCat or Project Panda, that use &quot;Sign in with Agent Native&quot; or
        ask for tokens. A redirect address must be exactly what the app sends: <code>https</code> only (plain <code>http</code> only
        for localhost), no wildcard, query or fragment, and no more than {MAX_REDIRECT_URIS} per app. The secret of a confidential
        app is shown once, when it is created or rotated; only a hash is stored. Apps that registered themselves (for example
        Claude connectors) are under Dynamic OAuth clients.
      </p>

      {secret && <OneTimeSecretAlert label="Client" clientId={secret.client_id} secret={secret.client_secret} onDismiss={() => setSecret(null)} />}
      {notice && (
        <Alert variant="success" title="Done">
          {notice}
        </Alert>
      )}
      {mutationError && (
        <Alert variant="danger" title="Action failed">
          {apiErrorMessage(mutationError.error)}
        </Alert>
      )}

      {form && (
        <Card padding="lg">
          <CardBody>
            <h2 className="mb-3 text-lg font-semibold">{form.clientId ? "Edit app" : "Register an app"}</h2>
            <form
              className="space-y-4"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                if (formValid) saveMutation.mutate(form);
              }}
            >
              <Input
                label="Client ID"
                value={form.newClientId}
                disabled={form.clientId !== null}
                error={idProblem ?? undefined}
                helperText="Optional. Convention: <app>-sparbz-cloud. Left empty, one is generated. It cannot be changed later."
                onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, newClientId: e.target.value })}
              />
              <Input label="Name" value={form.name} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })} />
              {form.clientId === null && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.isPublic} onChange={(e) => setForm({ ...form, isPublic: e.target.checked })} />
                  Public client (PKCE only, no secret). Choose this only for an app that cannot keep a secret.
                </label>
              )}
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Redirect addresses</legend>
                {form.uris.map((u, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="flex-1">
                      <Input
                        aria-label={`Redirect address ${i + 1}`}
                        value={u}
                        error={u !== "" ? (uriProblems[i] ?? undefined) : undefined}
                        helperText={i === 0 && u === "" ? "For example https://ledger.sparbz.cloud/auth/platform-link/callback" : undefined}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, uris: form.uris.map((x, j) => (j === i ? e.target.value : x)) })}
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={form.uris.length <= 1}
                      onClick={() => setForm({ ...form, uris: form.uris.filter((_, j) => j !== i) })}
                      aria-label={`Remove redirect address ${i + 1}`}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="secondary" disabled={form.uris.length >= MAX_REDIRECT_URIS} onClick={() => setForm({ ...form, uris: [...form.uris, ""] })}>
                  Add a redirect address
                </Button>
              </fieldset>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.shareOrgs} onChange={(e) => setForm({ ...form, shareOrgs: e.target.checked })} />
                May read the signed-in person&apos;s organization memberships (GET /oauth/orgs)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.refreshToken} onChange={(e) => setForm({ ...form, refreshToken: e.target.checked })} />
                May use refresh tokens
              </label>
              <div className="flex gap-2">
                <Button type="submit" disabled={!formValid || saveMutation.isPending}>
                  {form.clientId ? "Save changes" : "Register app"}
                </Button>
                <Button type="button" variant="secondary" onClick={() => setForm(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {clientsQuery.isLoading ? (
        <LoadingState message="Loading apps..." />
      ) : clientsQuery.isError ? (
        <Alert variant="danger" title="Failed to load apps">
          {apiErrorMessage(clientsQuery.error)}
        </Alert>
      ) : (clientsQuery.data ?? []).length === 0 ? (
        <EmptyState title="No registered apps" description="Register an app to let it sign people in or request tokens." />
      ) : (
        <Card padding="none">
          <DataTable<OperatorClient> data={clientsQuery.data!} columns={columns} emptyMessage="No apps." />
        </Card>
      )}

      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        title={pending ? pendingText[pending.kind].title : ""}
        message={pending ? pendingText[pending.kind].message(pending.client) : ""}
        confirmText={pending ? pendingText[pending.kind].confirm : "Confirm"}
        variant="danger"
        onConfirm={() => pending && actionMutation.mutate(pending)}
      />
    </div>
  );
}
